import { TranslationScopes } from 'app/common/translation-scopes';
import { Route } from '@angular/router';
import { IdentityProviders } from 'app/common/identity-providers';
import { AuthGuard } from 'app/core/guards/auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { environment } from 'environments/environment';
import { Auth0LoadGuard } from 'app/core/guards/auth0-load.guard';
import { PermissionScopes } from '@twrx-api-models';

import { TranslationGuard } from './core/guards/translation.guard';
import {
	AberrantQuantityRoutes,
	AccountRoutes,
	AuditEventsRoutes,
	Auth0Routes,
	BdcRoutes,
	CustomerMonthlyDataRoutes,
	CustomerRoutes,
	DownstreamClientIdAssociationRoutes,
	ExcludedPharmacyRoutes,
	FormularyNdcRoutes,
	InOutpatientDefinitionRoutes,
	LineOfBusinessRoutes,
	MaintenanceRoutes,
	NdcReplacementMappingsRoutes,
	NonPatientExclusionRoutes,
	NotFoundRoutes,
	PayorRoutes,
	ProcessingConfigRoutes,
	RebateAggregatorNdcRateRoutes,
	RebateAggregatorRoutes,
	RebateGroupAggregatorNdcRoutes,
	RebateGroupRoutes,
	RebatesLoadRoutes,
	RebatesReSubmissionRoutes,
	RebatesUpdateRoutes,
	ReportRoutes,
	SubmissionFilesRoutes,
	SubmissionOverviewRoutes,
	SubmissionPeriodRoutes,
	UnbreakableQuantityRoutes,
	UserRoutes
} from './common/routes';
import { AnonymousGuard } from './core/guards/anonymous.guard';
import {
	getExecutePermission,
	getReadPermission
} from './common/utils/utils.auth';

// @formatter:off

/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
	// Redirect empty path to '/example'
	{ path: '', pathMatch: 'full', redirectTo: CustomerMonthlyDataRoutes.Root },

	// Auth routes for guests and authenticated users
	{
		path:      '',
		component: LayoutComponent,
		data:      {
			layout: 'empty'
		},
		children: [
			{
				path:         MaintenanceRoutes.Root,
				loadChildren: () =>
					import('app/modules/auth/maintenance/maintenance.routes')
			}
		]
	},

	{
		path:        '',
		canActivate: [AuthGuard()],
		canMatch:
			environment.identityProvider === IdentityProviders.Auth0
				? [Auth0LoadGuard]
				: [],
		component: LayoutComponent,
		children:  [
			// Account routes
			{
				path:         AccountRoutes.Profile,
				canActivate:  [TranslationGuard(TranslationScopes.Account)],
				loadChildren: () =>
					import('app/modules/auth/profile-details/profile-details.routes')
			},
			{
				path:         AccountRoutes.NoPermissions,
				loadChildren: () =>
					import('app/modules/auth/no-permission/no-permission.routes')
			},

			{
				path:        ReportRoutes.Root,
				canActivate: [
					AuthGuard(getReadPermission(PermissionScopes.Report)),
					TranslationGuard(TranslationScopes.Report)
				],
				loadChildren: () => import('app/modules/report/report.routes')
			},

			// Customer Monthly Data routes
			{
				path:        CustomerMonthlyDataRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.CustomerMonthlyData)
					),
					TranslationGuard(TranslationScopes.CustomerMonthlyData)
				],
				loadChildren: () =>
					import('app/modules/customer-monthly-data/customer-monthly-data.routes')
			},

			//Ndc Replacement Mappings routes
			{
				path:        NdcReplacementMappingsRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(
							PermissionScopes.NdcReplacementMappings
						)
					),
					TranslationGuard(TranslationScopes.NdcReplacementMappings)
				],
				loadChildren: () =>
					import('app/modules/ndc-replacement-mappings/ndc-replacement-mappings.routes')
			},

			// Rebate Aggregators routes
			{
				path:        RebateAggregatorRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.RebateAggregators)
					),
					TranslationGuard(TranslationScopes.RebateAggregator)
				],
				loadChildren: () =>
					import('app/modules/rebate-aggregator/rebate-aggregator.routes')
			},

			// Rebate Aggregators Ndc Rates routes
			{
				path:        RebateAggregatorNdcRateRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(
							PermissionScopes.RebateAggregatorNdcRates
						)
					),
					TranslationGuard(
						TranslationScopes.RebateAggregatorsNdcRates
					)
				],
				loadChildren: () =>
					import('app/modules/rebate-aggregator-ndc-rate/rebate-aggregator-ndc-rate.routes')
			},

			// Formulary Ndc routes
			{
				path:        FormularyNdcRoutes.Root,
				canActivate: [
					AuthGuard(getReadPermission(PermissionScopes.FormularyNdc)),
					TranslationGuard(TranslationScopes.FormularyNdc)
				],
				loadChildren: () =>
					import('app/modules/formulary-ndc/formulary-ndc.routes')
			},

			// Submission period routes
			{
				path:        SubmissionPeriodRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.SubmissionPeriod)
					),
					TranslationGuard(TranslationScopes.SubmissionPeriod)
				],
				loadChildren: () =>
					import('app/modules/submission-period/submission-period.routes')
			},

			// Rebates Load routes
			{
				path:        RebatesLoadRoutes.Root,
				canActivate: [
					AuthGuard(getReadPermission(PermissionScopes.RebatesLoad)),
					TranslationGuard(
						TranslationScopes.RebatesLoad,
						TranslationScopes.InOutpatientDefinition,
						TranslationScopes.Facility
					)
				],
				loadChildren: () =>
					import('app/modules/rebate-load/rebate-load.routes')
			},

			// Rebates Update routes
			{
				path:        RebatesUpdateRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.RebatesUpdate)
					),
					TranslationGuard(TranslationScopes.RebatesUpdate)
				],
				loadChildren: () =>
					import('app/modules/rebates-update/rebates-update.routes')
			},

			// Rebates Re-Submission routes
			{
				path:        RebatesReSubmissionRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.RebatesReSubmission)
					),
					TranslationGuard(TranslationScopes.RebatesReSubmission)
				],
				loadChildren: () =>
					import('app/modules/rebates-re-submission/rebates-re-submission.routes')
			},

			// Payors routes
			{
				path:        PayorRoutes.Root,
				canActivate: [
					AuthGuard(getReadPermission(PermissionScopes.Payor)),
					TranslationGuard(TranslationScopes.Payors)
				],
				loadChildren: () => import('app/modules/payors/payors.routes')
			},

			// InOutpatientDefinition routes
			{
				path:        InOutpatientDefinitionRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(
							PermissionScopes.InOutpatientDefinition
						)
					),
					TranslationGuard(TranslationScopes.InOutpatientDefinition)
				],
				loadChildren: () =>
					import('app/modules/in-outpatient-definition/in-outpatient-definition.routes')
			},

			// Aberrant Quantity routes
			{
				path:        AberrantQuantityRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.AberrantQuantity)
					),
					TranslationGuard(TranslationScopes.AberrantQuantity)
				],
				loadChildren: () =>
					import('app/modules/aberrant-quantity/aberrant-quantity.routes')
			},

			// Line of business routes
			{
				path:        LineOfBusinessRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.LineOfBusiness)
					),
					TranslationGuard(TranslationScopes.LineOfBusiness)
				],
				loadChildren: () =>
					import('app/modules/line-of-business/line-of-business.routes')
			},

			// Bdc routes
			{
				path:        BdcRoutes.Root,
				canActivate: [
					AuthGuard(getReadPermission(PermissionScopes.ThirdParty)),
					TranslationGuard(TranslationScopes.Bdc)
				],
				loadChildren: () => import('app/modules/bdc/bdc.routes')
			},

			// User routes
			{
				path:        UserRoutes.Root,
				canActivate: [
					AuthGuard(getReadPermission(PermissionScopes.Users)),
					TranslationGuard(TranslationScopes.User)
				],
				loadChildren: () => import('app/modules/user/user.routes')
			},

			// Customer routes
			{
				path:        CustomerRoutes.Root,
				canActivate: [
					AuthGuard(getReadPermission(PermissionScopes.Customers)),
					TranslationGuard(TranslationScopes.Customer)
				],
				loadChildren: () =>
					import('app/modules/customer/customer.routes')
			},

			// Downstream Client Id Association routes
			{
				path:        DownstreamClientIdAssociationRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(
							PermissionScopes.DownstreamClientIdAssociation
						)
					),
					TranslationGuard(
						TranslationScopes.DownstreamClientIdAssociation
					)
				],
				loadChildren: () =>
					import('app/modules/downstream-client-id-association/downstream-client-id-association.routes')
			},

			// Unbreakable Quantity routes
			{
				path:        UnbreakableQuantityRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.UnbreakableQuantity)
					),
					TranslationGuard(TranslationScopes.UnbreakableQuantity)
				],
				loadChildren: () =>
					import('app/modules/unbreakable-quantity/unbreakable-quantity.routes')
			},

			// Non-Patient Exclusion routes
			{
				path:        NonPatientExclusionRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.NonPatientExclusion)
					),
					TranslationGuard(TranslationScopes.NonPatientExclusion)
				],
				loadChildren: () =>
					import('app/modules/non-patient-exclusion/non-patient-exclusion.routes')
			},

			// Rebate Group routes
			{
				path:        RebateGroupRoutes.Root,
				canActivate: [
					AuthGuard(getReadPermission(PermissionScopes.RebateGroup)),
					TranslationGuard(TranslationScopes.RebateGroup)
				],
				loadChildren: () =>
					import('app/modules/rebate-group/rebate-group.routes')
			},

			// Excluded Pharmacies routes
			{
				path:        ExcludedPharmacyRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.ExcludedPharmacy)
					),
					TranslationGuard(TranslationScopes.ExcludedPharmacy)
				],
				loadChildren: () =>
					import('app/modules/excluded-pharmacy/excluded-pharmacy.routes')
			},

			// Audit Events routes
			{
				path:        AuditEventsRoutes.Root,
				canActivate: [
					AuthGuard(getReadPermission(PermissionScopes.AuditEvents)),
					TranslationGuard(TranslationScopes.AuditEvents)
				],
				loadChildren: () =>
					import('app/modules/audit-events/audit-events.routes')
			},

			// Processing Config routes
			{
				path:        ProcessingConfigRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.ProcessingConfig)
					),
					TranslationGuard(TranslationScopes.ProcessingConfig)
				],
				loadChildren: () =>
					import('app/modules/processing-config/processing-config.routes')
			},

			// Rebate Load Group Aggregator NDC Routes
			{
				path:        RebateGroupAggregatorNdcRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(
							PermissionScopes.RebateGroupAggregatorNdc
						)
					),
					TranslationGuard(TranslationScopes.RebateGroupAggregatorNdc)
				],
				loadChildren: () =>
					import('app/modules/rebate-group-aggregator-ndc/rebate-group-aggregator-ndc.routes')
			},

			// Submission Files
			{
				path:        SubmissionFilesRoutes.Root,
				canActivate: [
					AuthGuard(
						getReadPermission(PermissionScopes.SubmissionFiles)
					),
					TranslationGuard(TranslationScopes.SubmissionFiles)
				],
				loadChildren: () =>
					import('app/modules/submission-files/submission-files.routes')
			},

			// Submission Overview
			{
				path:        SubmissionOverviewRoutes.Root,
				canActivate: [
					AuthGuard(
						getExecutePermission(
							PermissionScopes.SubmissionOverview
						)
					),
					TranslationGuard(TranslationScopes.SubmissionOverview)
				],
				loadChildren: () =>
					import('app/modules/submission-overview/submission-overview.routes')
			}
		]
	}
];

if (environment.identityProvider === IdentityProviders.Auth0) {
	appRoutes.push(
		// Auth routes for authenticated users only
		{
			path:        '',
			canActivate: [AuthGuard()],
			component:   LayoutComponent,
			data:        {
				layout: 'empty'
			},
			children: [
				{
					path:         Auth0Routes.Callback,
					loadChildren: () =>
						import('app/modules/auth/callback/callback.routes')
				}
			]
		},

		// Auth routes for guests and authenticated users
		{
			path:      '',
			component: LayoutComponent,
			data:      {
				layout: 'empty'
			},
			children: [
				{
					path:         Auth0Routes.AuthenticateError,
					loadChildren: () =>
						import('app/modules/auth/auth0-authenticate-error/auth0-authenticate-error.routes')
				}
			]
		},
		{
			path:      '',
			component: LayoutComponent,
			data:      {
				layout: 'empty'
			},
			children: [
				{
					path:         Auth0Routes.AccountLink,
					loadChildren: () =>
						import('app/modules/auth/auth0-account-link/auth0-account-link.routes')
				}
			]
		}
	);
} else {
	appRoutes.push(
		// Auth routes for guests only
		{
			path:        '',
			canActivate: [AnonymousGuard],
			component:   LayoutComponent,
			data:        {
				layout: 'empty'
			},
			children: [
				{
					path:         'confirmation-required',
					loadChildren: () =>
						import('app/modules/auth/confirmation-required/confirmation-required.routes')
				},
				{
					path:         AccountRoutes.ForgotPassword,
					loadChildren: () =>
						import('app/modules/auth/forgot-password/forgot-password.routes')
				},
				{
					path:         AccountRoutes.SignIn,
					loadChildren: () =>
						import('app/modules/auth/sign-in/sign-in.routes')
				},
				{
					path:         AccountRoutes.ConfirmPasswordReset,
					loadChildren: () =>
						import('app/modules/auth/password-reset-verify-code/password-reset-verify-code.routes')
				},
				{
					path:         AccountRoutes.ConfirmAccount,
					loadChildren: () =>
						import('app/modules/auth/password-reset-verify-code/password-reset-verify-code.routes')
				}
			]
		},

		// Auth routes for authenticated users only
		{
			path:        '',
			canActivate: [AuthGuard()],
			component:   LayoutComponent,
			data:        {
				layout: 'empty'
			},
			children: [
				{
					path:         AccountRoutes.SignOut,
					loadChildren: () =>
						import('app/modules/auth/sign-out/sign-out.routes')
				},
				{
					path:         AccountRoutes.ConfirmPasswordChange,
					loadChildren: () =>
						import('app/modules/auth/password-reset-verify-code/password-reset-verify-code.routes')
				}
			]
		}
	);
}

// Not Found Page
appRoutes.push(
	{
		path:       '**',
		redirectTo: NotFoundRoutes.Root,
		pathMatch:  'full'
	},
	{
		path:      '',
		component: LayoutComponent,
		data:      {
			layout: 'empty'
		},
		children: [
			{
				path:         NotFoundRoutes.Root,
				loadChildren: () =>
					import('app/modules/auth/not-found/not-found.routes')
			}
		]
	}
);
